import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { switchMap, map, catchError, of, withLatestFrom } from "rxjs";
import { PdfClient } from "src/api-service";
import { PdfTranslationService } from "src/pdf/services/pdf-translation.service";
import { normalizeError } from "src/app/store/app-store-error.types";
import { selectUserContext } from "src/app/store/user/user.selectors";
import { selectQuoteId } from "src/app/store/pages/quote/quote.selectors";
import { selectVersion } from "src/app/store/pages/quote/version/version.selectors";
import { PdfActions, PdfPublicActions } from "./pdf.actions";

// ============================================================================
// TOKEN DECODER - Extracts language for UI translations
// ============================================================================
function decodeTokenLanguage(token: string): string {
    const base64Url = token.split(".")[0];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
        atob(base64)
            .split("")
            .map(c => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join(""),
    );
    const decoded = JSON.parse(jsonPayload);
    return decoded.Language ?? "en";
}

@Injectable()
export class PdfEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly pdfClient = inject(PdfClient);
    private readonly pdfTranslationService = inject(PdfTranslationService);

    // =========================================================================
    // VIEW QUOTE - Token-based (Playwright, public links)
    // =========================================================================
    viewQuote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PdfPublicActions.viewQuote),
            switchMap(({ token }) => {
                const language = decodeTokenLanguage(token);
                return this.pdfTranslationService.loadLanguage(language).pipe(
                    switchMap(() =>
                        this.pdfClient.getPdfViewData("quote", token).pipe(
                            map(quote => PdfPublicActions.viewQuoteSuccess({ quote })),
                            catchError(error => of(PdfPublicActions.viewQuoteFailure({
                                error: normalizeError(error),
                            }))),
                        ),
                    ),
                );
            }),
        ),
    );

    // =========================================================================
    // VIEW VENDOR INQUIRY - Token-based (Playwright, public links)
    // =========================================================================
    viewVendorInquiry$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PdfPublicActions.viewVendorInquiry),
            switchMap(({ token }) => {
                const language = decodeTokenLanguage(token);
                return this.pdfTranslationService.loadLanguage(language).pipe(
                    switchMap(() =>
                        this.pdfClient.getPdfViewData("vendor-inquiry", token).pipe(
                            map(quote => PdfPublicActions.viewVendorInquirySuccess({ quote })),
                            catchError(error => of(PdfPublicActions.viewVendorInquiryFailure({
                                error: normalizeError(error),
                            }))),
                        ),
                    ),
                );
            }),
        ),
    );

    // =========================================================================
    // VIEW SUBSCRIPTION - Token-based (Playwright, public links)
    // =========================================================================
    viewSubscription$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PdfPublicActions.viewSubscription),
            switchMap(({ token }) => {
                const language = decodeTokenLanguage(token);
                return this.pdfTranslationService.loadLanguage(language).pipe(
                    switchMap(() =>
                        this.pdfClient.getPdfViewData("subscription", token).pipe(
                            map(quote => PdfPublicActions.viewSubscriptionSuccess({ quote })),
                            catchError(error => of(PdfPublicActions.viewSubscriptionFailure({
                                error: normalizeError(error),
                            }))),
                        ),
                    ),
                );
            }),
        ),
    );

    // =========================================================================
    // GET CURRENCY CONVERTED QUOTE - Generate token from store, then viewQuote
    // =========================================================================
    getCurrencyConvertedQuote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PdfActions.getCurrencyConvertedQuote),
            withLatestFrom(
                this.store.select(selectUserContext),
                this.store.select(selectQuoteId),
                this.store.select(selectVersion),
            ),
            switchMap(([, user, quoteId, version]) => {
                if (!quoteId || !version) {
                    return of(PdfActions.getCurrencyConvertedQuoteFailure({
                        error: { message: "Missing quote or version data" },
                    }));
                }

                return this.pdfClient.generatePdfToken(user.selectedCompany.id, {
                    quoteId,
                    versionId: version.id,
                    currency: version.currencyCode,
                    exchangeRate: version.currencyExchangeRateSnapshot ?? 1,
                    language: version.languageCode,
                    type: "quote",
                }).pipe(
                    map(tokenResponse => PdfPublicActions.viewQuote({ token: tokenResponse.token })),
                    catchError(error => of(PdfActions.getCurrencyConvertedQuoteFailure({
                        error: normalizeError(error),
                    }))),
                );
            }),
        ),
    );

    // =========================================================================
    // GET CURRENCY CONVERTED SUBSCRIPTION - Generate token, then viewSubscription
    // =========================================================================
    getCurrencyConvertedSubscription$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PdfActions.getCurrencyConvertedSubscription),
            withLatestFrom(
                this.store.select(selectUserContext),
                this.store.select(selectQuoteId),
                this.store.select(selectVersion),
            ),
            switchMap(([, user, quoteId, version]) => {
                if (!quoteId || !version) {
                    return of(PdfActions.getCurrencyConvertedSubscriptionFailure({
                        error: { message: "Missing quote or version data" },
                    }));
                }

                return this.pdfClient.generatePdfToken(user.selectedCompany.id, {
                    quoteId,
                    versionId: version.id,
                    currency: version.currencyCode,
                    exchangeRate: version.currencyExchangeRateSnapshot ?? 1,
                    language: version.languageCode,
                    type: "subscription",
                }).pipe(
                    map(tokenResponse => PdfPublicActions.viewSubscription({ token: tokenResponse.token })),
                    catchError(error => of(PdfActions.getCurrencyConvertedSubscriptionFailure({
                        error: normalizeError(error),
                    }))),
                );
            }),
        ),
    );

    // =========================================================================
    // LOAD VENDOR INQUIRY ITEMS - Generate token, then fetch items
    // =========================================================================
    loadVendorInquiryItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PdfActions.loadVendorInquiryItems),
            withLatestFrom(
                this.store.select(selectUserContext),
                this.store.select(selectQuoteId),
                this.store.select(selectVersion),
            ),
            switchMap(([, user, quoteId, version]) => {
                if (!quoteId || !version) {
                    return of(PdfActions.loadVendorInquiryItemsFailure({
                        error: { message: "Missing quote or version data" },
                    }));
                }

                return this.pdfClient.generatePdfToken(user.selectedCompany.id, {
                    quoteId,
                    versionId: version.id,
                    currency: version.currencyCode,
                    exchangeRate: version.currencyExchangeRateSnapshot ?? 1,
                    language: version.languageCode,
                    type: "vendor-inquiry",
                }).pipe(
                    switchMap(tokenResponse =>
                        this.pdfClient.getVendorInquiryItems(tokenResponse.token).pipe(
                            map(items => PdfActions.loadVendorInquiryItemsSuccess({ items })),
                        ),
                    ),
                    catchError(error => of(PdfActions.loadVendorInquiryItemsFailure({
                        error: normalizeError(error),
                    }))),
                );
            }),
        ),
    );
}
